// src/pages/Applications.js
//
// THE APPLICATION DESK (plan Part C; rulings 193, 194, 207, 208; task list section 348).
//
// Until today no screen in this platform showed an application to anybody. The read rule existed, the
// decide callable existed, and an application that arrived sat where nobody could see it.
//
// WHAT THIS PAGE DOES NOT DECIDE. Every rule it obeys lives in src\data\applicationDesk.js, which the Cloud
// Function reads too - who may act, what an exception is, and ruling 207.2's block on deciding while a
// question is open. This file renders that module's answers. If it disagreed with the server, the server
// would win, and the suite would go red before either of them reached a member.
import React, { useEffect, useMemo, useState } from 'react';
import DocumentReview from '../components/DocumentReview';   // section 543: spelling, grammar, facts, prose
import { factsFromRecord } from '../data/documentReview';   // section 543
import UnderConstruction from '../components/UnderConstruction';   // section 542
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { db, storage } from '../firebase';
import { localDesk, generalDesk, indicatorRows, actsFor, ACTS, decisionBlocked, exceptionReasons, rosterCrossReference } from '../data/applicationDesk';
import { gcDecides } from '../data/applicationApproval';   // Record 612 (ruling 291): who decides, said on the row
import { onboardingLetter, applicationHeaderBlocks, LETTER_INPUTS } from '../data/onboardingLetters';
import ObligationsPanel from '../components/ObligationsPanel';   // section 349: Part A on a screen
import { capScopes } from '../data/scopeCap';   // T5 item 20: the 30-territory cap, in one place
import RosterOnFilePanel from '../components/RosterOnFilePanel';   // ruling 330: the GC files a roster on file; the match runs at submit
import { deskLines as attestDeskLines, ATTEST_OFFICES } from '../data/attestationLink';   // ruling 330 Part 3: the officer attestation lines + offices

const GOLD = '#C9A84C';
const RED = '#E06A60';
const GREEN = '#40C070';
// Record 612 (item (c)): the seniority roster the platform holds for the cross-reference. NONE is held today - see
// applicationDesk.rosterCrossReference. Null means "not held", never an empty roster every number would fail.
const SENIORITY_ROSTER = null;

function Indicator({ row }) {
  const colour = row.matched === false ? RED : row.matched === true ? '#C8D8E8' : GOLD;
  const weight = row.matched === false ? 700 : 400;
  return (
    <tr>
      <td style={{ padding: '4px 10px 4px 0', color: '#8AA4BF', fontSize: 11, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{row.label}</td>
      <td style={{ padding: '4px 10px 4px 0', color: colour, fontWeight: weight, fontSize: 12.5 }}>{row.value || '-'}</td>
      <td style={{ padding: '4px 0', color: row.matched === false ? RED : '#6F8AA6', fontSize: 11 }}>{row.note}</td>
    </tr>
  );
}

export default function Applications({ user }) {
  const isGC = user?.role === 'general_chair' || user?.isAdmin;
  const [apps, setApps] = useState([]);
  const [obligations, setObligations] = useState({});
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  // SECTION 396. The one-time password a NEW member is given, shown on this screen and nowhere else.
  // It is not stored, not logged, and not e-mailed - the officer who approved him reads it to him.
  // Cleared the moment he moves to another application, so it cannot sit on a screen behind him.
  const [handover, setHandover] = useState(null);
  const [act, setAct] = useState('');
  const [text, setText] = useState('');
  const [correction, setCorrection] = useState({ field: 'rosterNumber', value: '', source: '' });
  const [letter, setLetter] = useState(null);
  const [attestations, setAttestations] = useState({});   // ruling 330 Part 3: applicationId -> attestation acts, for the desk
  const [attestForm, setAttestForm] = useState(null);     // { applicationId, office, email } while the GC issues a link

  // CORRECTED 2026-09-22 (section 364, R2). The comment that stood here said the rules would scope
  // this read and the query need not. THE OPPOSITE IS TRUE, and claimQueries.js says so at length:
  // Firestore proves a LISTING against the rule and refuses one that COULD return a document the
  // reader may not have. `where('status','==','pending')` alone could return another committee's
  // application, so it was denied for every LC and every GC - the desk section 348 built to stop
  // applications sitting where nobody could see them could not see them.
  //
  // Shaped to what the rules can prove: the GC asks for his committee, the LC asks once per
  // territory, and the rows are merged here. Equality only, so no composite index is needed.
  useEffect(() => {
    const ref = collection(db, 'applications');
    const isAdmin = !!(user && (user.isAdmin || user.role === 'admin'));
    let queries;
    if (isAdmin) {
      queries = [query(ref, where('status', '==', 'pending')), query(ref, where('gcFlag', '==', true))];
    } else if (user && user.role === 'general_chair' && user.committee) {
      // RECORD 588 (ruling 207.3 audit): the second query carries what 207.3 flags after it is decided (a refusal).
      // Equality only, and scoped to his committee, so the rule (inGCScope(generalCommittee)) can prove it.
      queries = [query(ref, where('generalCommittee', '==', user.committee), where('status', '==', 'pending')),
                 query(ref, where('generalCommittee', '==', user.committee), where('gcFlag', '==', true))];
    } else if (user && user.role === 'local_chair' && Array.isArray(user.scopes) && user.scopes.length) {
      queries = capScopes(user).scopes
        .map((sk) => query(ref, where('scopeKey', '==', sk), where('status', '==', 'pending')));
    } else {
      // Nobody else has an application desk. Saying so beats issuing a query that will be refused.
      setApps([]);
      return undefined;
    }
    // Each query reports independently; rows are merged by id so an application that matches two
    // territories is not shown twice.
    const byQuery = queries.map(() => []);
    const merge = () => {
      const seen = new Map();
      byQuery.forEach((rows) => rows.forEach((r) => seen.set(r.id, r)));
      setApps([...seen.values()]);
    };
    const unsubs = queries.map((q, i) => onSnapshot(q,
      (snap) => { byQuery[i] = snap.docs.map((d) => ({ id: d.id, ...d.data() })); merge(); },
      // A REFUSED READ IS NOT AN EMPTY DESK. Saying "no applications" to an officer who has some is
      // the failure this whole correction is about.
      (e) => setError(e.message || 'Could not read applications.')));
    return () => unsubs.forEach((u) => u());
  }, [user]);

  // SECTION 412, 2026-09-25. This was `onSnapshot(collection(db, 'obligations'))` with no filter,
  // and an error handler whose whole body was the comment "an officer with no obligations to read
  // is not an error". It swallowed the one thing it needed to report.
  //
  // MEASURED, not reasoned: scripts\audit-screen-queries.js ran this exact shape with a real
  // General Chairperson token and got HTTP 403. The obligations rule turns on
  // resource.data.scopeKey / generalCommittee, so an unfiltered listing cannot be proved. Every
  // officer's obligations strip has therefore been silently blank - not empty, REFUSED - while
  // telling him nothing was wrong.
  //
  // src\components\ObligationsPanel.js fixed exactly this in section 364 and shaped its queries to
  // what the rules can prove. This screen was missed by that sweep. Same shape, and the refusal is
  // now surfaced through setError like every other read on this page.
  useEffect(() => {
    const ref = collection(db, 'obligations');
    let qs;
    if (user && (user.isAdmin || user.role === 'admin')) qs = [query(ref)];
    else if (user && user.role === 'general_chair' && user.committee) qs = [query(ref, where('generalCommittee', '==', user.committee))];
    else if (user && user.role === 'local_chair' && Array.isArray(user.scopes) && user.scopes.length) {
      qs = capScopes(user).scopes.map((sk) => query(ref, where('scopeKey', '==', sk)));
    } else { setObligations({}); return undefined; }

    const buf = qs.map(() => []);
    const merge = () => {
      const by = {};
      buf.forEach((rows) => rows.forEach((v) => { if (v && v.memberUid) by[v.memberUid] = v; }));
      setObligations(by);
    };
    const unsubs = qs.map((q, i) => onSnapshot(q,
      (snap) => { buf[i] = snap.docs.map((d) => ({ id: d.id, ...d.data() })); merge(); },
      (e) => setError(e.message || 'The obligation records could not be read.')));
    return () => unsubs.forEach((u) => u());
  }, [user]);

  // RULING 330 Part 3: the attestations the routed officers have given, read by committee (GC) or by territory (LC), and
  // grouped by application so the desk shows them beside the match. Equality only, so no composite index is needed.
  useEffect(() => {
    const ref = collection(db, 'attestations');
    let qs;
    if (user && user.role === 'general_chair' && user.committee) qs = [query(ref, where('committee', '==', user.committee))];
    else if (user && user.role === 'local_chair' && Array.isArray(user.scopes) && user.scopes.length) qs = capScopes(user).scopes.map((sk) => query(ref, where('scopeKey', '==', sk)));
    else { setAttestations({}); return undefined; }
    const buf = qs.map(() => []);
    const merge = () => { const m = {}; buf.forEach((rows) => rows.forEach((x) => { (m[x.applicationId] = m[x.applicationId] || []).push(x); })); setAttestations(m); };
    const unsubs = qs.map((q, i) => onSnapshot(q, (snap) => { buf[i] = snap.docs.map((d) => ({ id: d.id, ...d.data() })); merge(); }, () => {}));
    return () => unsubs.forEach((u) => u());
  }, [user]);

  // THE CROSS-REFERENCE IS NOT PERFORMED YET (ONB-2 is blocked on a seniority roster the platform does not
  // hold - ruling 194). So every row is handed `null`, which the module reports as NOT CHECKED and never as
  // a pass. When the roster arrives, this is where its result is handed in, and nothing else changes.
  // Record 612 (Bob 10/8, item (c)): the cross-reference now RUNS, through applicationDesk.rosterCrossReference, against
  // the seniority roster the platform holds (SENIORITY_ROSTER). Today it holds none, so every row reads NOT PERFORMED and
  // says so; the day a roster is filed in data\ and projected here, the same call matches it. Never a block: the result
  // only colours the indicators and the exception queue.
  // RULING 330 Part 2: the cross-reference now RUNS at submit (functions submitApplication) against the rosters on file and
  // is stored on the application as rosterMatch, with the roster's status in words. The desk reads that result; it never
  // reads the roster itself (330.1). An application filed before any roster was on file has no rosterMatch, so it falls back
  // to the NOT-PERFORMED shape - never a pass it did not make. The GC re-checks pending applications with the panel below.
  const xrefs = useMemo(() => {
    const out = {};
    apps.forEach((a) => { out[a.id] = a.rosterMatch || rosterCrossReference(a, SENIORITY_ROSTER); });
    return out;
  }, [apps]);

  // Record 612: the action clock reads the time here, once per change of the desk (the module itself holds no clock).
  const rows = useMemo(() => {
    const nowMs = Date.now();
    return isGC ? generalDesk(apps, xrefs, nowMs) : localDesk(apps, xrefs, nowMs);
  }, [apps, xrefs, isGC]);
  const current = selected ? apps.find((a) => a.id === selected) || null : null;
  const actor = { uid: user?.uid, role: user?.isAdmin ? 'admin' : user?.role, committee: user?.committee || null, scopes: user?.scopes || [] };

  // RULING 330 Part 3: the GC issues a one-time link to the routed Local Chairperson or the Local's Secretary-Treasurer.
  const issueLink = async () => {
    if (!attestForm) return;
    setError(''); setNote('');
    try {
      const r = await httpsCallable(getFunctions(), 'issueAttestationLink')({ applicationId: attestForm.applicationId, office: attestForm.office, officerEmail: attestForm.email.trim() });
      setNote('A one-time confirmation link was sent to ' + r.data.sentTo + '. It works once and for 7 days.');
      setAttestForm(null);
    } catch (e) { setError((e && e.message) || 'The link could not be sent.'); }
  };

  const run = async () => {
    setError(''); setNote(''); setHandover(null);
    if (!current || !act) return;
    setBusy(true);
    try {
      const fns = getFunctions();
      if (act === 'approve' || act === 'refuse') {
        const fn = httpsCallable(fns, 'decideApplication');
        const res = await fn({ applicationId: current.id, decision: act === 'approve' ? 'approved' : 'refused', reason: text });
        setNote(act === 'approve' ? 'Approved. His account is open.' : 'Refused, with the reason on the record.');
        // Record 612 (item (d)): the claims he recorded with his application are now drafts in his account.
        if (res && res.data && res.data.draftClaimsCreated) {
          setNote((n) => n + ' ' + res.data.draftClaimsCreated + ' draft claim(s) were made in his account from the claims he recorded.');
        }
        if (res && res.data && res.data.reason) setNote((n) => n + ' ' + res.data.reason);
        // SECTION 396. Until today an approved man's account was created with NO PASSWORD and he was
        // left to the reset e-mail - which on this project cannot be repaired, and which tells a man
        // who has never had a password that he typed the wrong one. The officer hands it over instead.
        if (res && res.data && res.data.temporaryPassword) {
          setHandover({ pw: String(res.data.temporaryPassword), email: String(current.email || '') });
        }
      } else {
        const fn = httpsCallable(fns, 'applicationAct');
        const res = await fn({ applicationId: current.id, kind: act, text, correction: act === 'correct' ? correction : null });
        setNote(res && res.data && res.data.gcFlagged
          ? 'Done, and flagged for the General Chairperson. [R-207]'
          : 'Done.');
      }
      setText(''); setAct('');
    } catch (e) {
      setError((e && e.message) || 'That act was refused.');
    } finally {
      setBusy(false);
    }
  };

  const buildLetter = (kind) => {
    const blocks = applicationHeaderBlocks(current, {});
    const input = {};
    (LETTER_INPUTS[kind] || []).forEach((k) => { input[k] = ''; });
    setLetter(onboardingLetter(kind, current, blocks, input));
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 style={{ margin: 0 }}>Applications</h1>
        <p>
          {isGC
            ? 'Every application in your committee. Flagged ones first. [R-207]'
            : 'What needs you. An application whose every value matches carries itself. [R-194]'}
        </p>
      </div>

      {error && <div className="alert-banner">&#9888; {error}</div>}
      {note && <div className="alert-banner" style={{ borderColor: GREEN, color: GREEN }}>{note}</div>}
      {handover && (
        <div style={{ margin: '10px 0 14px', padding: '12px 16px', borderRadius: 6,
                      background: '#2D1F0A', border: '2px solid #E8A020', color: '#FFD27A' }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>READ THIS TO HIM. IT IS NOT SHOWN AGAIN.</div>
          <div style={{ fontFamily: 'monospace', fontSize: 22, letterSpacing: 2, color: '#FFFFFF', margin: '10px 0' }}>
            {String(handover.pw).replace(/(.{4})/g, '$1 ').trim()}
          </div>
          <div style={{ fontWeight: 400, color: '#D8E2EE', fontSize: 12.5 }}>
            His sign-in is <b>{handover.email}</b>. Lower case and digits only, with no l, no 1, no o
            and no 0, so it can be said down a telephone without being misheard.
          </div>
          <div style={{ fontWeight: 400, color: '#D8E2EE', fontSize: 12.5, marginTop: 6 }}>
            <b>Do not e-mail it.</b> Tell him to change it once he is in — right now two people know
            his password, and only one of them should.
          </div>
          <button type="button" onClick={() => setHandover(null)}
            style={{ marginTop: 10, background: 'none', border: '1px solid #E8A020', color: '#FFD27A',
                     borderRadius: 4, padding: '4px 12px', fontSize: 11.5, cursor: 'pointer' }}>
            I have given it to him — hide it
          </button>
        </div>
      )}

      <RosterOnFilePanel user={user} />

      {rows.length === 0 && <UnderConstruction note="No application has been received yet." />}
      {rows.length === 0 && (
        <div className="empty-state">
          <p>{isGC ? 'No applications are open in your committee.' : 'Nothing needs you here today.'}</p>
        </div>
      )}

      {rows.map(({ application, reasons, flagged, flagReason, clock }) => {
        const open = selected === application.id;
        const obligation = obligations[application.approvedUid || ''] || null;
        const blocked = decisionBlocked(application);
        return (
          <div key={application.id} className="card" style={{ marginBottom: 14, borderColor: flagged ? GOLD : undefined }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
              <div>
                <strong style={{ fontSize: 15 }}>{application.fullLegalName || '(no name given)'}</strong>
                <span style={{ color: '#6F8AA6', fontSize: 12, marginLeft: 8 }}>{application.id.slice(0, 8).toUpperCase()}</span>
              </div>
              <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => { setSelected(open ? null : application.id); setLetter(null); setAct(''); }}>
                {open ? 'Close' : 'Open'}
              </button>
            </div>

            {flagged && (
              <div style={{ color: GOLD, fontSize: 11.5, marginTop: 6 }}>
                FLAGGED: {flagReason || application.gcFlagReason || 'less than approval'} [R-207]
              </div>
            )}
            {/* Record 612: the action clock (abeyance with a timer), and who decides it (ruling 291). */}
            {clock && application.status === 'pending' && (
              <div style={{ color: clock.overdue ? RED : '#8AA4BF', fontSize: 11.5, marginTop: 6, fontWeight: clock.overdue ? 700 : 400 }}>
                {clock.label}
              </div>
            )}
            {application.status === 'pending' && gcDecides(application) && (
              <div style={{ color: GOLD, fontSize: 11.5, marginTop: 6 }}>
                Decided by the General Chairperson: {gcDecides(application)}. [R-291]
              </div>
            )}
            {(reasons || exceptionReasons(application, null)).length > 0 && (
              <div style={{ color: '#8AA4BF', fontSize: 11.5, marginTop: 6 }}>
                {(reasons || []).join(' - ')}
              </div>
            )}
            {blocked.blocked && (
              <div style={{ color: GOLD, fontSize: 11.5, marginTop: 6 }}>{blocked.why}</div>
            )}

            {open && (
              <div style={{ marginTop: 12 }}>
                {/* Section 352: the roster he attached, opened by the officers the rules admit and nobody
                    else. The link is a signed URL from Storage; if he is not one of them it is refused
                    there, not hidden here. */}
                {application.exhibitPath && (
                  <div style={{ fontSize: 11.5, color: '#8AA4BF', marginBottom: 8 }}>
                    Seniority roster attached by the applicant (requirements v4).{' '}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const url = await getDownloadURL(storageRef(storage, application.exhibitPath));
                          window.open(url, '_blank', 'noopener');
                        } catch (e) {
                          setError('That exhibit could not be opened: ' + ((e && e.message) || 'refused'));
                        }
                      }}
                      style={{ background: 'none', border: 'none', color: '#4A9EE8', fontSize: 11.5, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                    >
                      Open it
                    </button>
                  </div>
                )}
                {application.exhibitError && (
                  <div style={{ fontSize: 11.5, color: '#E06A60', marginBottom: 8 }}>
                    He attached a roster and it did not store: {application.exhibitError}
                  </div>
                )}
                {/* Record 612 (item (c)): the roster cross-reference, from the roster the platform holds - never a block. */}
                <div style={{ fontSize: 11.5, color: (xrefs[application.id] && xrefs[application.id].performed) ? '#C8D8E8' : GOLD, marginBottom: 8 }}>
                  Seniority roster: {(xrefs[application.id] && xrefs[application.id].summary) || 'Not checked.'}
                </div>
                {/* RULING 330 Part 3: officer attestation by one-time link - the answers given, and (GC) a control to ask for one. */}
                {(() => {
                  const lines = attestDeskLines(attestations[application.id] || []);
                  return (
                    <div style={{ fontSize: 11.5, marginBottom: 10 }}>
                      {lines.map((l, i) => <div key={i} style={{ color: '#C8D8E8' }}>Attestation: {l}</div>)}
                      {isGC ? (
                        attestForm && attestForm.applicationId === application.id ? (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
                            <select aria-label="Which officer" className="form-select" style={{ maxWidth: 190 }} value={attestForm.office} onChange={(e) => setAttestForm({ ...attestForm, office: e.target.value })}>
                              {Object.entries(ATTEST_OFFICES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <input aria-label="Officer's e-mail of record" className="form-input" style={{ maxWidth: 230 }} type="email" placeholder="officer's e-mail of record" value={attestForm.email} onChange={(e) => setAttestForm({ ...attestForm, email: e.target.value })} />
                            <button className="btn btn-primary" style={{ fontSize: 11 }} disabled={!attestForm.email.trim()} onClick={issueLink}>Send one-time link</button>
                            <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setAttestForm(null)}>Cancel</button>
                          </div>
                        ) : (
                          <button className="btn btn-secondary" style={{ fontSize: 11, marginTop: 6 }} onClick={() => setAttestForm({ applicationId: application.id, office: 'lc', email: '' })}>Ask a Local officer to confirm (one-time link)</button>
                        )
                      ) : null}
                    </div>
                  );
                })()}
                {/* Section 806 (Bob 2026-10-26): the tests run while his General Chairperson was not on the platform - flags, never a refusal. */}
                {application.unboardedChecks && application.unboardedChecks.applies && (
                  <div style={{ fontSize: 11.5, color: '#C8D8E8', marginBottom: 10 }}>
                    <div style={{ color: '#8AA4BF', marginBottom: 4 }}>
                      Checks run because his General Chairperson was not on the platform: {application.unboardedChecks.summary}. A flag is not a refusal.
                    </div>
                    {(application.unboardedChecks.results || []).map((t) => (
                      <div key={t.key} style={{ color: t.result === 'flag' ? GOLD : t.result === 'pass' ? '#C8D8E8' : '#8AA4BF' }}>
                        {t.result === 'flag' ? 'Flagged' : t.result === 'pass' ? 'Passed' : 'Not run'}: {t.test}{t.note ? ' - ' + t.note : ''}
                      </div>
                    ))}
                  </div>
                )}
                {/* Record 612 (item (d)): claims he recorded with the application - held, not filed, until it is decided. */}
                {Array.isArray(application.pendingClaims) && application.pendingClaims.length > 0 && (
                  <div style={{ fontSize: 11.5, color: '#C8D8E8', marginBottom: 10 }}>
                    <div style={{ color: '#8AA4BF', marginBottom: 4 }}>
                      Claims he recorded with this application - held, not filed. If he is approved, each becomes a draft claim in his account.
                    </div>
                    {application.pendingClaims.map((c, i) => (
                      <div key={i} style={{ borderLeft: '2px solid ' + GOLD, paddingLeft: 8, marginBottom: 6 }}>
                        <b>{c.date}</b>{c.location ? ' - ' + c.location : ''}{c.kind ? ' [' + c.kind + ']' : ''}
                        <div style={{ whiteSpace: 'pre-wrap' }}>{c.whatHappened}</div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Record 612 (ruling 207.3 'Reviewed'): every flag cleared, with its note - kept on the record. */}
                {Array.isArray(application.gcFlagReviews) && application.gcFlagReviews.length > 0 && (
                  <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 8 }}>
                    {application.gcFlagReviews.map((r, i) => (
                      <div key={i}>Reviewed{r.automatic ? ' (automatically)' : ''} {String(r.at || '').slice(0, 10)}: {r.reason ? r.reason + ' - ' : ''}{r.note}</div>
                    ))}
                  </div>
                )}
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    {indicatorRows(application, xrefs[application.id] || null, obligation).map((r) => <Indicator key={r.key} row={r} />)}
                  </tbody>
                </table>

                <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {actsFor(actor, application).map((a) => (
                    <button key={a.kind} type="button" className="btn btn-secondary"
                      disabled={!a.available || busy}
                      title={a.available ? (a.flagsGc ? 'Flags the General Chairperson [R-207]' : '') : a.why}
                      onClick={() => { setAct(a.kind); setText(''); }}
                      style={{ fontSize: 11, opacity: a.available ? 1 : 0.45 }}>
                      {a.label}
                    </button>
                  ))}
                </div>

                {act && (
                  <div style={{ marginTop: 12 }}>
                    <label className="form-label">{ACTS[act].label}{ACTS[act].flagsGc ? ' - this flags the General Chairperson' : ''}</label>
                    <textarea aria-label="What to say to the applicant" className="form-textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)}
                      placeholder={act === 'ask_gc' ? 'What are you asking him?' : act === 'answer' ? 'Your answer' : act === 'review' ? 'A short note: what you reviewed, and why the flag can be cleared' : 'Say why, in your own words'} />
                    {act === 'correct' && (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <select aria-label="Which field needs correcting" className="form-input" style={{ maxWidth: 200 }} value={correction.field}
                          onChange={(e) => setCorrection((c) => ({ ...c, field: e.target.value }))}>
                          {['rosterNumber', 'seniorityDate', 'workLocation', 'employeeNumber', 'jobSymbol'].map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                        <input aria-label="corrected value" className="form-input" style={{ maxWidth: 200 }} placeholder="corrected value"
                          value={correction.value} onChange={(e) => setCorrection((c) => ({ ...c, value: e.target.value }))} />
                        <input aria-label="checked against [R-194]" className="form-input" style={{ maxWidth: 260 }} placeholder="checked against [R-194]"
                          value={correction.source} onChange={(e) => setCorrection((c) => ({ ...c, source: e.target.value }))} />
                      </div>
                    )}
                    <button className="btn btn-primary" style={{ marginTop: 8, fontSize: 12 }} disabled={busy || !text.trim()} onClick={run}>
                      {busy ? 'Working...' : 'Do it'}
                    </button>
                  </div>
                )}

                <div style={{ marginTop: 14, borderTop: '1px solid rgba(201,168,76,0.25)', paddingTop: 10 }}>
                  <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 6 }}>
                    LETTERS - assembled from this record, never written by a model. Fill what is
                    missing before you hand one over. [R-208]
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {['notice_to_appear', 'approval', 'refusal', 'referral', 'correction', 'obligation_filed'].map((k) => (
                      <button key={k} type="button" className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => buildLetter(k)}>
                        {k.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                  {letter && letter.ok && (
                    <div style={{ marginTop: 10 }}>
                      {!letter.sendable && (
                        <div style={{ color: GOLD, fontSize: 11.5, marginBottom: 6 }}>
                          NOT READY TO HAND OVER - missing: {letter.missingFields.join(', ') || letter.unresolvedBlocks.join(', ')}
                        </div>
                      )}
                      <pre style={{ whiteSpace: 'pre-wrap', background: '#0E1318', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 6, padding: 14, fontSize: 12, lineHeight: 1.5 }}>
                        {letter.letter.text}
                      </pre>
                      <DocumentReview title="this letter" kind="letter" text={letter.letter.text} facts={factsFromRecord(current)} today={new Date().toISOString().slice(0, 10)} />
                    </div>
                  )}
                  {letter && !letter.ok && <div style={{ color: RED, fontSize: 11.5, marginTop: 6 }}>{letter.reason}</div>}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Plan Part A on a screen: who has taken the obligation, who has not, and the form that files it. */}
      <ObligationsPanel user={user} />
    </div>
  );
}
